import React from "react"
import { SafeAreaView, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native"
import { useIsFocused } from '@react-navigation/native'
import { DrawerContentComponentProps, DrawerContentOptions } from "@react-navigation/drawer"
import { AppContext, AuthService, Category, DatePicker, ErrorHandling, form2, GeneralService, HistoricTask, Input, InquiryComplaintService, Permission, PulseOpsFormat, PulseOpsService, RadioButton, RBAC, Select, SelectOption, SelectSearch, ServiceInquiryCategories, ServiceInquiryNote, sharedStyle, SourceList, TaskComment, TaskOp, TaskService, TaskSource, TransactionLabel, TransactionType, TransactionTypeSelectOptions } from "@pulseops/common"
import { TFunction, useTranslation } from "react-i18next"
import { pipe } from "fp-ts/lib/function"
import { ZIO } from "@mxt/zio"
import { TabSearch } from "../service-inquiry/basic-search-form"
import { SectionCol, SectionRow } from "@pulseops/submission/common"
import { SearchFields } from "@pulseops/outbound"
import { Controller, useForm } from "react-hook-form"
import { adminClaimSearchFieldList, advancedSearchFieldList, BasicFieldTypeNames, basicSearchFieldList, claimSearchFieldList, FieldSearch, getFieldSearch } from "./BasicSearchFormFields"
import _ from "lodash"
import { BasicSearchConst } from "./BasicSearchConst"
import { useLoading } from "@mxt/zio-react"
import { BasicSearchTable } from "./BasicSearchTable"
import { CommentPopup } from "./CommentPopup"
import { ServiceInquiryNotePopup } from "./ServiceInquiryNotePopup"
import { ViewUploadedDocumentPopup } from "./ViewUploadedDocumentPopup"
import { LinearProgress } from "@material-ui/core"
import { AdminClaimSearchTable } from "./admin-claim-search"
import { ClaimSearchTable } from "./claim-search"
import { DistributionServiceInquirySearch, DSTable, DSTableRef, IDCTable, IDCTableRef, IndependenceCheckInquirySearch, PremiumCollectionSearch, PremiumTable, PremiumTableRef } from "../service-inquiry/service-inquiry-screen"
import { basicSharedStyles } from "./BasicSharedStyles"
import { NewBasicSearchForm } from "./NewBasicSearchForm"

function getDefaultTab(permissions: string[]): TabSearch | null {
  if (permissions.includes(Permission['ViewAdminSearchServiceInquiry'])) {
    return TabSearch.AdminClaimSearch
  } else if (permissions.includes(Permission['ViewTraditionalSearchServiceInquiry'])) {
    return TabSearch.TraditionalClaimSearch
  } else if (permissions.includes(Permission['ViewBasicSearchServiceInquiry'])) {
    return TabSearch.BasicSearch
  } else if (permissions.includes(Permission['ViewAdvancedSearchServiceInquiry'])) {
    return TabSearch.AdvancedSearch
  } else if (permissions.includes(Permission['ViewClaimSearchServiceInquiry'])) {
    return TabSearch.ClaimSearch
  } else if (permissions.includes(Permission['ViewPremiumCollectionSearch'])) {
    return TabSearch.PremiumCollection
  } else if (permissions.includes(Permission['ViewDistributionServicesServiceInquiry'])) {
    return TabSearch.DistributionServicesSearch
  } else {
    return null
  }
}

export const ServiceInquiryForCompletedCase = (props: DrawerContentComponentProps<DrawerContentOptions>) => {
  const isFocused = useIsFocused()
  const { t, i18n } = useTranslation()
  const { changeBreadcrumb } = React.useContext(AppContext.AppContextInstance)
  const [tab, setTab] = React.useState<TabSearch | null>(null)
  const [officeGA, setOfficeGA] = React.useState<SelectOption | null>(null)
  const [errorMessageForGA, setErrorMessageForGA] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<TaskOp[]>([])
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [totalItem, setTotalItem] = React.useState<number>(0)
  const [subServiceTypeList, setSubServiceTypeList] = React.useState<SelectOption[]>([])
  const [subTransactionTypeList, setSubTransactionTypeList] = React.useState<(SelectOption & { typeCode: string })[]>([])
  const [officeCodes, setOfficeCodes] = React.useState<SelectOption[]>([])
  const [messageResponse, setMessageResponse] = React.useState<string | null>(null)
  const [isLoading, bindLoader] = useLoading(false)
  const [PSComment, setPSComment] = React.useState<boolean>(false)
  const [PSCommentData, setPSCommentData] = React.useState<TaskComment[]>([])
  const { showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const [infoVisible, setInfoVisible] = React.useState<boolean>(false)
  const [uploadDocVisible, setUploadDocVisible] = React.useState<boolean>(false)
  const [uploadDocumentTaskId, setUploadDocumentTaskId] = React.useState<string>()
  const [isUploadImaging, setIsUploadImaging] = React.useState<boolean>(false)
  const [claimTypes, setClaimTypes] = React.useState<{ label: string; value: string; codes: any[] }[]>([])
  const [noteDetail, setNoteDetail] = React.useState<ServiceInquiryNote>({
    customerComment: '',
    closedInfo: '',
    responseToCustomer: ''
  })
  const [sortItem, setSortItem] = React.useState<{ field: string; order: 'asc' | 'desc' | undefined }>({
    field: '',
    order: 'asc'
  })
  const [showExportBtn, setShowExportBtn] = React.useState<boolean>(false)
  const premiumRef = React.useRef<PremiumTableRef | null>(null)
  const DSRef = React.useRef<DSTableRef | null>(null)
  const IDCRef = React.useRef<IDCTableRef | null>(null)
  const currentDate = new Date()
  const defaultValues: NewBasicSearchForm.Raw = {
    policyNumber: '',
    policyNumberClaim: '',
    idNumber: '',
    clientNumber: '',
    phoneNumber: '',
    source: [],
    category: ServiceInquiryCategories[0],
    transactionType:
      tab === TabSearch.ClaimSearch
        ? [{
          label: t(`TransactionType:${TransactionType.NEW_CLAIM_REGISTER}`),
          value: TransactionType.NEW_CLAIM_REGISTER
        }]
        : [],
    fromDate: currentDate,
    toDate: currentDate,
    subServiceType: [],
    subTransactionType: [],
    offices: officeGA ? [officeGA] : [],
    claimNumber: '',
    claimTypes: [],
    status: null
  }
  const { base, handleSubmit } = form2.useForm(NewBasicSearchForm.codec, {
    defaultValues: defaultValues
  })

  const roles: string[] = pipe(
    RBAC.permissionWithNoCache,
    ZIO.map((permissions) => {
      setTab(getDefaultTab(permissions))
      return permissions
    }),
    ErrorHandling.runDidMount([])
  )

  React.useEffect(() => {
    setPageSize(10)
    setPageNum(0)
    setTotalItem(0)
    //  setIsCollapse(false)
    setInfoVisible(false)
    setRows([])
    setMessageResponse(null)
    setErrorMessageForGA(null)
    //  setSubmittedData({})
    setSortItem({ field: '', order: 'asc' })
    base.reset(defaultValues)
    !!tab && NewBasicSearchForm.setTab(tab)
  }, [tab])

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            props.navigation.navigate('HomeScreen')
          }
        },
        {
          title: t('menu:ServiceInquiryForCompletedCase'),
          navigate: null
        }
      ])
      // load Sub Service Type
      pipe(
        InquiryComplaintService.getTypes(),
        ZIO.map((typeList) => {
          if (typeList && typeList.length > 0) {
            setSubServiceTypeList(
              typeList.slice().map((x) => ({
                label: i18n.language === 'en' ? x.name : x.nameVi,
                value: x.code
              }))
            )
          } else {
            setSubServiceTypeList([])
          }
        }),
        ZIO.unsafeRun({})
      )
      // load sub transaction type
      pipe(
        InquiryComplaintService.getSubType(),
        ZIO.map((typeList) => {
          if (typeList && typeList.length > 0) {
            setSubTransactionTypeList(
              typeList.slice().map((x) => ({
                label: i18n.language === 'en' ? x.name : x.nameVi,
                value: x.code,
                typeCode: x.typeCode
              }))
            )
          } else {
            setSubTransactionTypeList([])
          }
        }),
        ZIO.unsafeRun({})
      )
      // load getOfficeCodes
      pipe(
        GeneralService.getALLOfficeList(),
        ZIO.map((officeList) => {
          return officeList && officeList.length > 0 ? officeList.filter((x) => ['CSC', 'PCM'].includes(x.type)) : []
        }),
        ZIO.zipWith(AuthService.userInfo),
        ZIO.flatMap(([codes, userInfo]) =>
          pipe(
            userInfo.officeCode ? GeneralService.getOfficeCode(userInfo.officeCode) : ZIO.succeed({ body: [] }),
            ZIO.map((officeGA) => ({ codes, userInfo, officeGA: officeGA.body }))
          )
        ),
        ZIO.map(({ codes, userInfo, officeGA }) => {
          setOfficeCodes(
            codes.map((code) => ({
              value: code.code,
              label: i18n.language === 'vi' ? code.nameVi : code.nameEn
            }))
          )
          if (userInfo.officeCode) {
            const office = officeCodes.find((code) => code.value === userInfo.officeCode)
            if (office) {
              setOfficeGA(office)
              base.setValue('offices', [office])
            } else {
              const office = {
                value: userInfo.officeCode,
                label: (i18n.language === 'en' ? officeGA[0]?.nameEn : officeGA[0]?.nameVi) || userInfo.officeCode
              }
              setOfficeCodes([office])
              setOfficeGA(office)
              base.setValue('offices', [office])
            }
          }
        }),
        ZIO.unsafeRun({})
      )

      pipe(
        GeneralService.getClaims,
        ZIO.map((codes) => {
          setClaimTypes(
            _.map(_.groupBy(codes, 'claimTypeShortName'), (item, key) => ({
              value: key,
              label: i18n.language === 'en' ? item[0]?.claimType : item[0]?.claimTypeVN,
              codes: _.groupBy(codes, 'claimTypeShortName')[key]
            }))
          )
        }),
        ZIO.unsafeRun({})
      )
    }
    return () => {
      setPSComment(false)
      setPSCommentData([])
      setMessageResponse(null)
      setInfoVisible(false)
      setNoteDetail({
        customerComment: '',
        closedInfo: '',
        responseToCustomer: ''
      })
      setUploadDocVisible(false)
      setUploadDocumentTaskId('')
      setIsUploadImaging(false)
      setShowExportBtn(false)
    }
  }, [isFocused])



  const submitSearch = (_data: NewBasicSearchForm.Validated) => {
    setPageNum(0)
    setMessageResponse(null)
    getServiceInquiry(pageSize, 0)
  }

  const search = () => {
    // Keep NewBasicSearchForm tab in sync right before validation runs.
    NewBasicSearchForm.setTab(tab)
    return handleSubmit(submitSearch)()
  }

  const getSubmitData = (data: NewBasicSearchForm.Raw): HistoricTask.Filter => {
    const customedFromDate = data.fromDate || new Date()
    const customedToDate = data.toDate || new Date()
    switch (tab) {
      case TabSearch.BasicSearch: {
        return {
          policyNum: data.policyNumber || undefined,
          idNumber: data.idNumber ? data.idNumber.trim() : undefined,
          clientNum: data.clientNumber || undefined,
          phoneNumber: data.phoneNumber || undefined,
          fromDate: PulseOpsFormat.serviceInquiry_StartOfDate(customedFromDate, true),
          toDate: PulseOpsFormat.serviceInquiry_endOfDate(customedToDate, true),
          source: data.source.length > 0 ? data.source.map((x) => x.value as TaskSource) : undefined,
          categories: data.category?.value
            ? [data.category.value]
            : [
              Category.COMPLAINT,
              Category.INQUIRY,
              Category.POLICY_SERVICE,
              Category.GENERAL,
              Category.PREMIUM_COLLECTION
            ],
          transactionType:
            data.transactionType.length > 0 ? data.transactionType.map((x) => x.value as TransactionType) : undefined,
          subServiceTypes: data.subServiceType.length > 0 ? data.subServiceType.map((x) => x.value) : undefined,
          subTransactionTypes:
            data.subTransactionType.length > 0 ? data.subTransactionType.map((x) => x.value) : undefined
        }
      }
      case TabSearch.AdvancedSearch: {
        return {
          fromDate: PulseOpsFormat.serviceInquiry_StartOfDate(customedFromDate, true),
          toDate: PulseOpsFormat.serviceInquiry_endOfDate(customedToDate, true),
          source: data.source.length > 0 ? data.source.map((x) => x.value as TaskSource) : undefined,
          offices: data.offices.length > 0 ? data.offices.map((x) => x.value) : undefined,
          categories: data.category?.value
            ? [data.category.value]
            : [
              Category.COMPLAINT,
              Category.INQUIRY,
              Category.POLICY_SERVICE,
              Category.GENERAL,
              Category.PREMIUM_COLLECTION
            ],
          transactionType:
            data.transactionType.length > 0 ? data.transactionType.map((x) => x.value as TransactionType) : undefined,
          subServiceTypes: data.subServiceType.length > 0 ? data.subServiceType.map((x) => x.value) : undefined,
          subTransactionTypes:
            data.subTransactionType.length > 0 ? data.subTransactionType.map((x) => x.value) : undefined
        }
      }
      case TabSearch.ClaimSearch: {
        return {
          policyNum: data.policyNumberClaim || undefined,
          claimNumber: data.claimNumber || undefined,
          clientNum: data.idNumber || undefined, // idNumber = clientNum
          fromDate: PulseOpsFormat.serviceInquiry_StartOfDate(customedFromDate, true),
          toDate: PulseOpsFormat.serviceInquiry_endOfDate(customedToDate, true),
          source: data.source.length > 0 ? data.source.map((x) => x.value as TaskSource) : undefined,
          claimTypes:
            data.claimTypes.length > 0
              ? _.flatten(data.claimTypes.map((x) => x.codes.map((_) => _.claimCode)))
              : undefined,
          categories: ['CL'],
          transactionType:
            data.transactionType.length > 0 ? data.transactionType.map((x) => x.value as TransactionType) : undefined
        }
      }
      case TabSearch.AdminClaimSearch: {
        return {
          policyNum: data.policyNumberClaim || undefined,
          claimNumber: data.claimNumber || undefined,
          clientNum: data.idNumber || undefined, // idNumber = clientNum
          fromDate: PulseOpsFormat.serviceInquiry_StartOfDate(customedFromDate, true),
          toDate: PulseOpsFormat.serviceInquiry_endOfDate(customedToDate, true),
          source: data.source.length > 0 ? data.source.map((x) => x.value as TaskSource) : undefined,
          claimTypes:
            data.claimTypes.length > 0
              ? _.flatten(data.claimTypes.map((x) => x.codes.map((_) => _.claimCode)))
              : undefined,
          categories: ['CL'],
          transactionType:
            data.transactionType.length > 0 ? data.transactionType.map((x) => x.value as TransactionType) : undefined
        }
      }
      case TabSearch.TraditionalClaimSearch: {
        return {
          policyNum: data.policyNumberClaim || undefined,
          claimNumber: data.claimNumber || undefined,
          clientNum: data.idNumber || undefined, // idNumber = clientNum
          fromDate: PulseOpsFormat.serviceInquiry_StartOfDate(customedFromDate, true),
          toDate: PulseOpsFormat.serviceInquiry_endOfDate(customedToDate, true),
          source: data.source.length > 0 ? data.source.map((x) => x.value as TaskSource) : undefined,
          claimTypes:
            data.claimTypes.length > 0
              ? _.flatten(data.claimTypes.map((x) => x.codes.map((_) => _.claimCode)))
              : undefined,
          categories: ['CL']
        }
      }
      default:
        return {}
    }
  }

  const getServiceInquiry = async (pageSize: number, pageNum: number) => {
    const isValid = await base.trigger()
    if (isValid) {
      const formData = base.getValues()
      const submittedData = getSubmitData(formData)
      pipe(
        tab === TabSearch.TraditionalClaimSearch
          ? TaskService.getHistoricTraditionalClaimTasks({
            pagination: {
              pageIndex: pageNum,
              pageSize: pageSize
            },
            order: 'desc',
            filter: submittedData
          })
          : TaskService.getHistoricCompleteTaskList({
            pagination: {
              pageIndex: pageNum,
              pageSize: pageSize
            },
            order: 'desc',
            filter: submittedData
          }),
        ZIO.tap((res) => {
          setTotalItem(res.total)
          setRows(
            res.items.map((item) => ({
              ...item,
              categoryName: BasicSearchConst.convertCategoryToLabel(item.category),
              transactionTypeName: t(TransactionLabel(item.transactionType)),
              subServiceTypeName: subServiceTypeList.find((x) => x.value === item.subServiceType)?.label || '-',
              subTransactionTypeName:
                subTransactionTypeList.find((x) => x.value === item.subTransactionType)?.label || '-',
              lastUpdateByVerName: item.lastUpdateByAdj
                ? item.lastUpdateByAdj.split('@')[0]
                : item.lastUpdateByVer
                  ? item.lastUpdateByVer.split('@')[0]
                  : '-'
            }))
          )
          setMessageResponse(res.items.length === 0 ? t('message:MS030029') : null)
          return ZIO.unit
        }),
        bindLoader,
        ErrorHandling.run()
      )
    }
  }

  const getCommentByTaskID = (processId: string) => {
    showGlobalLoading(true)
    return pipe(
      PulseOpsService.getCommentList(processId),
      ZIO.tap((comments) => {
        showGlobalLoading(false)
        setPSComment(true)
        setPSCommentData(comments ?? [])
        return ZIO.unit
      }),
      ZIO.mapError((e) => {
        showGlobalLoading(false)
        return ZIO.fail(e)
      }),
      ZIO.unsafeRun({})
    )
  }

  const getServiceInquiryNoteByTaskID = (taskId: string) => {
    pipe(
      TaskService.getServiceInquiryNote(taskId),
      ZIO.tap((note) => {
        setNoteDetail({
          customerComment: note.customerComment,
          closedInfo: note.closedInfo,
          responseToCustomer: note.responseToCustomer
        })
        setInfoVisible(true)
        return ZIO.unit
      }),
      bindLoader,
      ErrorHandling.run()
    )
  }

  const openUploadDocumentModal = (taskId: string, uploadImaging?: boolean) => {
    setUploadDocVisible(true)
    setUploadDocumentTaskId(taskId)
    setIsUploadImaging(uploadImaging ?? false)
  }

  const getSearchBlocks = () => {
    switch (tab) {
      case TabSearch.ClaimSearch:
      case TabSearch.AdminClaimSearch:
      case TabSearch.AdvancedSearch:
      case TabSearch.BasicSearch: {
        return (
          <View style={serviceStyles.searchedFieldContainer}>
            {getSearchComponents()}
            <View style={[serviceStyles.buttonContainer, sharedStyle.secondLine]}>
              <TouchableOpacity
                style={[sharedStyle.button, sharedStyle.btnRed, serviceStyles.buttonContent1]}
                onPress={search}
              >
                <Text style={sharedStyle.textButtonRed}>{t('common:Search')}</Text>
              </TouchableOpacity>
            </View>
            <View>
              <Text style={serviceStyles.errorMessage}>
                {_.get(base.formState.errors, '.message') || errorMessageForGA}
              </Text>
            </View>
          </View>
        )
      }
      case TabSearch.PremiumCollection: {
        return (
          <PremiumCollectionSearch
            onSearch={(data) => {
              const submitData: HistoricTask.Filter = {
                policyNum: data.policyNumber ?? undefined,

                clientNum: data.clientNumber ?? undefined, // idNumber = clientNum
                fromDate: data.fromDate
                  ? PulseOpsFormat.serviceInquiry_StartOfDate(data.fromDate, true)
                  : undefined,
                toDate: data.toDate ? PulseOpsFormat.serviceInquiry_endOfDate(data.toDate, true) : undefined,
                amount: data.amount ?? undefined,
                bankCode: data.bankCode?.value ?? undefined,
                referenceNumber: data.referenceNumber ?? undefined,
                proposalNumber: data.proposalNumber ?? undefined,
                categories: ['PC'],
                isCallingNewServiceInquiryApi: true
              }
              // setSubmittedData(submitData)
              // getPremiumCollectionTask(submitData, pageSize, 0)
              premiumRef.current?.onSearch(submitData)
            }}
          />
        )
      }
      case TabSearch.DistributionServicesSearch: {
        return (
          <DistributionServiceInquirySearch
            onSearch={(data) => {
              const submitData: HistoricTask.Filter = {
                eref: data.eRef ?? undefined,
                agentCode: data.agentCode ?? undefined,
                clientNum: data.IDnumber ? data.IDnumber.trim() : undefined,
                phoneNumber: data.phoneNumber || undefined,
                fromDate: data.fromDate ?? undefined,
                toDate: data.toDate ?? undefined,
                categories: ['DS'],
                source: !!data?.source ? data.source.map((x) => x.value as TaskSource) : undefined,
                transactionType: !!data?.transactionType
                  ? data.transactionType.map((x) => x.value as TransactionType)
                  : undefined,
                isCallingNewServiceInquiryApi: true
              }
              DSRef.current?.onSearch?.(submitData)
            }}
            onExport={() => {
              DSRef.current?.onExport?.()
            }}
            isShowBtnExport={showExportBtn}
          />
        )
      }
      case TabSearch.IndependenceCheck: {
        return (
          <IndependenceCheckInquirySearch
            onSearch={(data) => {
              const submitData: HistoricTask.Filter = {
                proposalNumber: data.ProposalNumber ?? undefined,
                // clientNum: data.IDnumber ? data.IDnumber.trim() : undefined,
                fromDate: data.fromDate ?? undefined,
                toDate: data.toDate ?? undefined,
                // categories: ['IDC'],
                source: !!data?.source ? data.source.map((x) => x.value as TaskSource) : undefined,
                // transactionType: !!data?.transactionType
                //   ? data.transactionType.map((x) => x.value as TransactionType)
                //   : undefined,
                transactionType: [data.transactionType?.value as TransactionType],
                policyNum: data.PolicyNumber ?? undefined,
                idNumber: data.IDnumber ? data.IDnumber.trim() : undefined,
                category: 'IDC',
                taxNumber: data.transactionType?.value === TransactionType.VOICE_RECORDING && data.taxNumber ? data.taxNumber : undefined,
                agentCertificate: data.transactionType?.value === TransactionType.VOICE_RECORDING && data.agentLicense ? data.agentLicense : undefined,
                signDateCompany: data.transactionType?.value === TransactionType.VOICE_RECORDING && data.signDateCompany ? data.signDateCompany : undefined,
                isCallingNewServiceInquiryApi: true
              }
              IDCRef.current?.onSearch?.(submitData)
            }}
            onExport={() => {
              IDCRef.current?.onExport?.()
            }}
            isShowBtnExport={showExportBtn}
          />
        )
      }
      default:
        return <></>
    }
  }

  const getSearchComponents = () => {
    switch (tab) {
      case TabSearch.BasicSearch:
        return (
          <SectionRow>
            {basicSearchFieldList(t, base, base.watch("category"), subServiceTypeList, subTransactionTypeList).map((i) => {
              const isShowedComponent = (i.formName !== 'transactionType' && i.formName !== 'subServiceType' && i.formName !== 'subTransactionType')
                || (i.formName === 'transactionType' && !!base.watch("category")?.value)
                || (['subServiceType'].includes(i.formName) && [Category.INQUIRY, Category.COMPLAINT].includes(base.watch("category")?.value as Category))
                || (['subTransactionType'].includes(i.formName) && base.watch('subServiceType').length > 0)
              return isShowedComponent ? (
                <SectionCol sectionStyles={serviceStyles.colMarginBottom} key={i.formName}>
                  <Controller
                    name={i.formName as BasicFieldTypeNames}
                    control={base.control}
                    // rules={i.formName === 'fromDate' || i.formName === 'toDate' ? Object.assign({ ...i.rules }, validation(i.formName)) : i.rules}
                    render={(controller) => getFieldSearch({ field: i, controller })}
                  />
                </SectionCol>
              ) : <></>
            })}
          </SectionRow>
        )
      case TabSearch.AdvancedSearch: {
        return (
          <SectionRow>
            {advancedSearchFieldList(t, base, officeCodes, subServiceTypeList, subTransactionTypeList).map((i) => {
              const isShowedComponent = (i.formName !== 'transactionType' && i.formName !== 'subServiceType' && i.formName !== 'subTransactionType')
                || (i.formName === 'transactionType' && !!base.watch("category")?.value)
                || (['subServiceType'].includes(i.formName) && [Category.INQUIRY, Category.COMPLAINT].includes(base.watch("category")?.value as Category))
                || (['subTransactionType'].includes(i.formName) && base.watch('subServiceType').length > 0)
              return isShowedComponent ? (
                <SectionCol sectionStyles={serviceStyles.colMarginBottom} key={i.formName}>
                  <Controller
                    name={i.formName as BasicFieldTypeNames}
                    control={base.control}
                    // rules={i.formName === 'fromDate' || i.formName === 'toDate' ? Object.assign({ ...i.rules }, validation(i.formName)) : i.rules}
                    render={(controller) => getFieldSearch({ field: i, controller })}
                  />
                </SectionCol>
              ) : <></>
            })}
          </SectionRow>
        )
      }
      case TabSearch.AdminClaimSearch: {
        return (
          <SectionRow>
            {adminClaimSearchFieldList(t, claimTypes).map((i) => {
              return (
                <SectionCol sectionStyles={serviceStyles.colMarginBottom} key={i.formName}>
                  <Controller
                    name={i.formName as BasicFieldTypeNames}
                    control={base.control}
                    // rules={i.formName === 'fromDate' || i.formName === 'toDate' ? Object.assign({ ...i.rules }, validation(i.formName)) : i.rules}
                    render={(controller) => getFieldSearch({ field: i, controller })}
                  />
                </SectionCol>
              )
            })}
          </SectionRow>
        )
      }
      case TabSearch.ClaimSearch: {
        return (
          <SectionRow>
            {claimSearchFieldList(t, claimTypes).map((i) => {
              return (
                <SectionCol sectionStyles={serviceStyles.colMarginBottom} key={i.formName}>
                  <Controller
                    name={i.formName as BasicFieldTypeNames}
                    control={base.control}
                    render={(controller) => getFieldSearch({ field: i, controller })}
                  />
                </SectionCol>
              )
            })}
          </SectionRow>
        )
      }
      default:
        return <></>
    }
  }

  const getSearchedTableByTab = () => {
    switch (tab) {
      case TabSearch.AdminClaimSearch: {
        return (
          <>
            {rows && rows.length > 0 ? (
              <AdminClaimSearchTable
                rows={rows}
                roles={roles}
                parentNavigation={props}
                selectedTab={tab}
                pageNum={pageNum}
                setPageNum={(pageNo) => setPageNum(pageNo)}
                pageSize={pageSize}
                setPageSize={(pageS) => setPageSize(pageS)}
                totalItem={totalItem}
                sortItem={sortItem}
                setSortItem={(value) => setSortItem(value)}
                queryServiceInquiryList={getServiceInquiry}
              />
            ) : (
              <View style={serviceStyles.errorMessageContent}>
                <Text style={serviceStyles.errorMessage}>{messageResponse}</Text>
              </View>
            )}
          </>
        )
      }
      case TabSearch.BasicSearch:
      case TabSearch.AdvancedSearch: {
        return (
          <>
            {rows && rows.length > 0 ? (
              <BasicSearchTable
                rows={rows}
                setRows={setRows}
                roles={roles}
                parentNavigation={props}
                pageNum={pageNum}
                setPageNum={(pageNo) => setPageNum(pageNo)}
                pageSize={pageSize}
                setPageSize={(pageS) => setPageSize(pageS)}
                totalItem={totalItem}
                sortItem={sortItem}
                setSortItem={(value) => setSortItem(value)}
                queryServiceInquiryList={getServiceInquiry}
                getCommentByTaskID={getCommentByTaskID}
                getServiceInquiryNoteByTaskID={getServiceInquiryNoteByTaskID}
                openUploadDocumentModal={openUploadDocumentModal}
                subServiceTypeList={subServiceTypeList}
                subTransactionTypeList={subTransactionTypeList}
              />
            ) : (
              <View style={serviceStyles.errorMessageContent}>
                <Text style={serviceStyles.errorMessage}>{messageResponse}</Text>
              </View>
            )}
          </>
        )
      }
      case TabSearch.ClaimSearch: {
        return (
          <>
            {rows && rows.length > 0 ? (
              <ClaimSearchTable
                rows={rows}
                roles={roles}
                parentNavigation={props}
                selectedTab={tab}
                pageNum={pageNum}
                setPageNum={(pageNo) => setPageNum(pageNo)}
                pageSize={pageSize}
                setPageSize={(pageS) => setPageSize(pageS)}
                totalItem={totalItem}
                sortItem={sortItem}
                setSortItem={(value) => setSortItem(value)}
                queryServiceInquiryList={getServiceInquiry}
                claimTypeList={claimTypes}
              />
            ) : (
              <View style={serviceStyles.errorMessageContent}>
                <Text style={serviceStyles.errorMessage}>{messageResponse}</Text>
              </View>
            )}
          </>
        )
      }
      case TabSearch.PremiumCollection: {
        return <PremiumTable ref={premiumRef} />
      }
      case TabSearch.DistributionServicesSearch: {
        return <DSTable ref={DSRef} onShowBtnExport={setShowExportBtn} />
      }
      case TabSearch.IndependenceCheck: {
        return <IDCTable ref={IDCRef} onShowBtnExport={setShowExportBtn} />
      }
      default:
        return <></>
    }
  }

  return (
    <SafeAreaView style={serviceStyles.container}>
      <ScrollView>
        <View style={serviceStyles.searchContainer}>
          <ScrollView style={serviceStyles.tabContainer} horizontal={true} showsHorizontalScrollIndicator={true}>
            <View style={[serviceStyles.row, serviceStyles.tabContent]}>
              {roles.includes(Permission['ViewAdminSearchServiceInquiry']) && (
                <View style={[tab === TabSearch.AdminClaimSearch && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                  <RadioButton
                    key="BasicSearch"
                    label={t('ServiceInquiry:AdminClaimSearch')}
                    selected={tab === TabSearch.AdminClaimSearch}
                    onChange={() => setTab(TabSearch.AdminClaimSearch)}
                    labelStyle={basicSharedStyles.cellNoWrap}
                  />
                </View>
              )}
              {roles.includes(Permission['ViewBasicSearchServiceInquiry']) && (
                <View style={[tab === TabSearch.BasicSearch && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                  <RadioButton
                    key="BasicSearch"
                    label={t('ServiceInquiry:BasicSearch')}
                    selected={tab === TabSearch.BasicSearch}
                    onChange={() => setTab(TabSearch.BasicSearch)}
                    labelStyle={basicSharedStyles.cellNoWrap}
                  />
                </View>
              )}
              {roles.includes(Permission['ViewAdvancedSearchServiceInquiry']) && (
                <View style={[tab === TabSearch.AdvancedSearch && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                  <RadioButton
                    key="AdvancedSearch"
                    label={t('ServiceInquiry:AdvancedSearch')}
                    selected={tab === TabSearch.AdvancedSearch}
                    onChange={() => setTab(TabSearch.AdvancedSearch)}
                    labelStyle={basicSharedStyles.cellNoWrap}
                  />
                </View>
              )}
              {roles.includes(Permission['ViewClaimSearchServiceInquiry']) && (
                <View style={[tab === TabSearch.ClaimSearch && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                  <RadioButton
                    key="ClaimSearch"
                    label={t('ServiceInquiry:ClaimSearch')}
                    selected={tab === TabSearch.ClaimSearch}
                    onChange={() => setTab(TabSearch.ClaimSearch)}
                    labelStyle={basicSharedStyles.cellNoWrap}
                  />
                </View>
              )}
              {roles.includes(Permission['ViewPremiumCollectionSearch']) && (
                <View style={[tab === TabSearch.PremiumCollection && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                  <RadioButton
                    key={'ServiceInquiry'}
                    label={t('ServiceInquiry:PremiumCollectionSearch')}
                    selected={tab === TabSearch.PremiumCollection}
                    onChange={() => setTab(TabSearch.PremiumCollection)}
                    labelStyle={basicSharedStyles.cellNoWrap}
                  />
                </View>
              )}
              {roles.includes(Permission['ViewDistributionServicesServiceInquiry']) && (
                <View style={[tab === TabSearch.DistributionServicesSearch && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                  <RadioButton
                    key={'ServiceInquiry'}
                    label={t('ServiceInquiry:DistributionService')}
                    selected={tab === TabSearch.DistributionServicesSearch}
                    onChange={() => setTab(TabSearch.DistributionServicesSearch)}
                    labelStyle={basicSharedStyles.cellNoWrap}
                  />
                </View>
              )}
              {(roles.includes(Permission['View-IndependenceCheck']) ||
                roles.includes(Permission['IDC-Query']) ||
                roles.includes(Permission['ViewIndependenceCheckServiceInquiry'])) && (
                  <View style={[tab === TabSearch.IndependenceCheck && serviceStyles.selectedTabContent, serviceStyles.tabBlock]}>
                    <RadioButton
                      key={'ServiceInquiry'}
                      label={t('ServiceInquiry:IndependenceCheck')}
                      selected={tab === TabSearch.IndependenceCheck}
                      onChange={() => setTab(TabSearch.IndependenceCheck)}
                    />
                  </View>
                )}
            </View>
          </ScrollView>
          {getSearchBlocks()}
        </View>
        {isLoading && <LinearProgress color="secondary" />}
        {getSearchedTableByTab()}
        <CommentPopup
          PSComment={PSComment}
          PSCommentData={PSCommentData}
          isLoading={isLoading}
          setPSComment={(value: boolean) => setPSComment(value)}
          setPSCommentData={(value: TaskComment[]) => setPSCommentData(value)}
        />
        <ServiceInquiryNotePopup
          infoVisible={infoVisible}
          isLoading={isLoading}
          setInfoVisible={setInfoVisible}
          noteDetail={noteDetail}
        />
        <ViewUploadedDocumentPopup
          uploadDocVisible={uploadDocVisible}
          setUploadDocVisible={setUploadDocVisible}
          uploadDocumentTaskId={uploadDocumentTaskId}
          isLoading={isLoading}
          isUploadImaging={isUploadImaging}
        />
      </ScrollView>
    </SafeAreaView>
  )
}
const serviceStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFF'
  },
  searchContainer: {
    backgroundColor: '#FAFAFA',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D3DCE6',
    marginHorizontal: 30,
    // marginVertical: 30,
    marginBottom: 30,
    paddingBottom: 16
  },
  tabContainer: {
    backgroundColor: '#eaeaea',
  },
  tabContent: {
    // backgroundColor: '#d9d6d6',
    // paddingHorizontal: 0,
    // marginBottom: 16
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tabBlock: {
    padding: 16,
  },
  selectedTabContent: {
    backgroundColor: '#FAFAFA',
  },
  searchedFieldContainer: {
    display: 'flex',
    paddingHorizontal: 20,
    marginTop: 20,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  buttonContent1: {
    marginVertical: 0,
    marginLeft: 0
  },
  colMarginBottom: {
    marginBottom: 15
  },

  errorMessage: {
    color: '#ED1B2E'
  },
  errorMessageContent: {
    marginHorizontal: 30
  },
  radioButton: {
    marginRight: 0
  }
})